import type { ReactNode } from 'react';
import type { EctdFrontMatter, EctdSourceEntry } from '@/features/dossier/api';
import { RegaraNote, TitledSection } from './DossierPrimitives';

/**
 * The bibliography and closing disclaimer of a U.S. drug / biologic eCTD, as the PDF prints
 * them, from `ectd_front_matter` on the run response (built once by the backend, the same data
 * the PDF and the Word file read); nothing here is worded, ordered or filtered on the page.
 * The cover and Document Control are download-only (PDF and Word), by founder decision
 * 2026-11-01. The section navigation stands in for the PDF's contents page.
 */

// A source's address is the model's text: only a web address becomes a link.
const WEB_ADDRESS_RE = /^https?:\/\//i;

function SourceLine({ entry }: { entry: EctdSourceEntry }) {
  const parts: ReactNode[] = [];
  if (entry.title) parts.push(entry.title);
  if (entry.identifier) parts.push(`(${entry.identifier})`);
  if (entry.url) {
    parts.push(
      WEB_ADDRESS_RE.test(entry.url) ? (
        <a key="url" href={entry.url} target="_blank" rel="noopener noreferrer" className="text-electric underline break-all">
          {entry.url}
        </a>
      ) : (
        entry.url
      ),
    );
  }
  if (entry.note) parts.push(`— ${entry.note}`);
  return (
    <li className="my-1">
      {parts.map((part, i) => (
        <span key={i}>
          {i > 0 ? ' ' : ''}
          {part}
        </span>
      ))}
    </li>
  );
}

export function EctdBibliography({ frontMatter }: { frontMatter: EctdFrontMatter }) {
  const { title, entries } = frontMatter.bibliography;
  if (entries.length === 0) return null;
  return (
    <TitledSection title={title}>
      <ol className="list-decimal pl-5 m-0 text-[13.5px]">
        {entries.map((entry, i) => (
          <SourceLine key={i} entry={entry} />
        ))}
      </ol>
    </TitledSection>
  );
}

/** The closing disclaimer in a REGARA note box, as the Word file prints it (founder 2026-11-02): it is
 *  REGARA's text, not part of submission. */
export function EctdDisclaimer({ frontMatter }: { frontMatter: EctdFrontMatter }) {
  const { lead, text } = frontMatter.disclaimer;
  return <RegaraNote caption={lead} body={text} />;
}
